(() => {
  if (customElements.get('aplovio-home-carousel')) return;
  const mobileQuery = '(max-width: 749px)';
  const reducedQuery = '(prefers-reduced-motion: reduce)';

  class HomeCarousel extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.track = this.querySelector('[data-home-track]');
      if (!this.track) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.mobile = matchMedia(mobileQuery);
      this.reduced = matchMedia(reducedQuery);
      this.controlSets = [...this.querySelectorAll('[data-home-controls]')];
      this.prev = this.querySelector('[data-home-prev]');
      this.next = this.querySelector('[data-home-next]');
      this.counter = this.querySelector('[data-home-counter]');
      this.dots = this.querySelector('[data-home-dots]');
      this.dots?.addEventListener('aplovio:indicator-select', event => {
        this.pauseByUser();
        this.go(event.detail.index, true);
      }, {signal});
      this.autoplayButton = this.querySelector('[data-home-autoplay]');
      this.status = this.querySelector('[data-home-status]');
      this.inView = false;
      this.userPaused = false;
      this.hovered = false;
      this.points = [];
      this.index = 0;
      this.scheduleRefresh = () => {
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => { this.frame = 0; this.refresh(); });
      };
      if (this.dots) window.AplovioLazy.near(this, () => {
        this.indicatorsNear = true;
        this.scheduleRefresh();
      }, {signal});
      this.mobile.addEventListener('change', this.scheduleRefresh, {signal});
      this.reduced.addEventListener('change', this.scheduleRefresh, {signal});
      this.track.addEventListener('scroll', () => this.update(), {passive: true, signal});
      this.addEventListener('pointerenter', e => { if (e.pointerType === 'mouse') { this.hovered = true; this.schedule(); } }, {signal});
      this.addEventListener('pointerleave', () => { this.hovered = false; this.schedule(); }, {signal});
      this.addEventListener('focusin', () => this.pauseByUser(), {signal});
      this.track.addEventListener('pointerdown', () => this.pauseByUser(), {passive: true, signal});
      this.track.addEventListener('wheel', () => this.pauseByUser(), {passive: true, signal});
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !this.active) return;
        const delta = {ArrowRight: this.rtl ? -1 : 1, ArrowLeft: this.rtl ? 1 : -1}[event.key];
        if (delta || event.key === 'Home' || event.key === 'End') {
          event.preventDefault(); this.pauseByUser();
          this.go(event.key === 'Home' ? 0 : event.key === 'End' ? this.points.length - 1 : this.index + delta, true);
        }
      }, {signal});
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button === this.prev || button === this.next) {
          this.pauseByUser(); this.go(this.index + (button === this.next ? 1 : -1), true);
        } else if (button === this.autoplayButton) {
          this.userPaused = !this.userPaused; this.schedule();
        }
      }, {signal});
      this.addEventListener('shopify:block:select', event => {
        const tile = event.target.closest('[data-home-slide], .ap-product-card');
        if (!tile || !this.track.contains(tile)) return;
        this.pauseByUser();
        if (this.active) tile.scrollIntoView({behavior: 'instant', block: 'nearest', inline: 'nearest'});
      }, {signal});
      document.addEventListener('visibilitychange', () => this.schedule(), {signal});
      this.resize = new ResizeObserver(this.scheduleRefresh);
      this.resize.observe(this.track);
      this.mutations = new MutationObserver(this.scheduleRefresh);
      this.mutations.observe(this.track, {childList: true});
      this.visibility = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting; this.schedule();
      }, {threshold: 0.1});
      this.visibility.observe(this);
      this.scheduleRefresh();
    }
    refresh() {
      if (!this.isConnected) return;
      const layout = this.mobile.matches ? this.dataset.mobileLayout : this.dataset.desktopLayout;
      this.active = layout === 'slider' && this.track.scrollWidth > this.track.clientWidth + 2;
      this.rtl = getComputedStyle(this.track).direction === 'rtl';
      const rect = this.track.getBoundingClientRect();
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const position = Math.abs(this.track.scrollLeft);
      const columns = Math.max(1, Math.floor(parseFloat(getComputedStyle(this.track).getPropertyValue('--ap-home-cols')) || 1));
      const items = Array.from(this.track.children).filter(node => node.getBoundingClientRect().width > 0);
      // Arrows move by the visible group, or by one item.
      const step = this.dataset.step === 'card' ? 1 : columns;
      this.points = [0];
      for (let i = step; i < items.length; i += step) {
        const item = items[i].getBoundingClientRect();
        const point = Math.min(max, Math.max(0, position + (this.rtl ? rect.right - item.right : item.left - rect.left)));
        if (point > this.points.at(-1) + 2) this.points.push(point);
      }
      if (max > this.points.at(-1) + 2) this.points.push(max);
      for (const set of this.controlSets) set.hidden = !this.active;
      // Arrows over the image edges sit on the middle of the first image, not of its caption.
      const media = this.dataset.arrowPosition === 'edges' && items[0]?.querySelector('.ap-home-tile__visual, .ap-card-media, .ap-home-media');
      if (media) {
        const box = media.getBoundingClientRect(), frame = this.track.parentElement.getBoundingClientRect();
        this.style.setProperty('--ap-home-arrow-top', `${Math.round(box.top - frame.top + box.height / 2 - 22)}px`);
      }
      this.track.tabIndex = this.active ? 0 : -1;
      if (this.dots) {
        this.dots.dataset.count = String(this.points.length);
        if (this.indicatorsNear && this.active && !this.indicatorLoading) {
          this.indicatorLoading = window.AplovioLazy.component(this.dots).catch(() => {
            console.warn('[Aplovio] Carousel indicators unavailable; arrows and scrolling remain usable.');
          });
        }
      }
      this.update(); this.schedule();
    }
    update() {
      if (!this.points?.length) return;
      const position = Math.abs(this.track.scrollLeft);
      this.index = this.points.reduce((best, point, i) => Math.abs(point - position) < Math.abs(this.points[best] - position) ? i : best, 0);
      if (this.prev) this.prev.disabled = this.dataset.loop !== 'true' && this.index === 0;
      if (this.next) this.next.disabled = this.dataset.loop !== 'true' && this.index === this.points.length - 1;
      if (this.counter) this.counter.textContent = `${String(this.index + 1).padStart(2, '0')} / ${String(this.points.length).padStart(2, '0')}`;
      if (this.dots) this.dots.dataset.index = String(this.index);
    }
    go(index, announce = false) {
      if (!this.active) return;
      const count = this.points.length;
      const target = this.dataset.loop === 'true' ? (index + count) % count : Math.min(count - 1, Math.max(0, index));
      this.track.scrollTo({left: this.points[target] * (this.rtl ? -1 : 1), behavior: this.reduced.matches ? 'instant' : 'smooth'});
      if (announce && this.status) this.status.textContent = this.status.dataset.label.replace('[current]', String(target + 1)).replace('[total]', String(count));
    }
    pauseByUser() { this.userPaused = true; this.schedule(); }
    schedule() {
      clearTimeout(this.timer);
      const disabled = this.reduced?.matches || window.Shopify?.designMode;
      if (this.autoplayButton) {
        this.autoplayButton.hidden = !!disabled;
        this.autoplayButton.setAttribute('aria-label', this.userPaused ? this.autoplayButton.dataset.playLabel : this.autoplayButton.dataset.pauseLabel);
        const play = this.autoplayButton.querySelector('[data-home-play]');
        const pause = this.autoplayButton.querySelector('[data-home-pause]');
        if (play) play.hidden = !this.userPaused;
        if (pause) pause.hidden = this.userPaused;
      }
      if (!this.active || this.dataset.autoplay !== 'true' || disabled || this.userPaused || this.hovered || !this.inView || document.hidden) return;
      if (this.index === this.points.length - 1 && this.dataset.loop !== 'true') return;
      const delay = Math.min(15, Math.max(3, Number(this.dataset.interval) || 6)) * 1000;
      this.timer = setTimeout(() => { this.go(this.index + 1); this.schedule(); }, delay);
    }
    disconnectedCallback() {
      this.abort?.abort(); this.abort = null;
      this.resize?.disconnect(); this.mutations?.disconnect(); this.visibility?.disconnect();
      clearTimeout(this.timer); cancelAnimationFrame(this.frame);
    }
  }

  class HomeMedia extends HTMLElement {
    connectedCallback() {
      if (this.abort || this.dataset.kind !== 'video' || !this.querySelector('template[data-home-video]')) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.mobile = matchMedia(mobileQuery); this.reduced = matchMedia(reducedQuery);
      this.toggle = this.querySelector('[data-home-video-toggle]');
      this.userPaused = false; this.inView = false;
      this.mobile.addEventListener('change', () => { if (this.inView || this.video) this.mount(); }, {signal});
      this.reduced.addEventListener('change', () => this.sync(), {signal});
      document.addEventListener('visibilitychange', () => this.sync(), {signal});
      this.toggle?.addEventListener('click', () => {
        if (!this.video) return;
        this.userPaused = !this.video.paused;
        if (this.video.paused) this.play(); else this.video.pause();
      }, {signal});
      this.observer = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        if (this.inView) this.mount();
        this.sync();
      }, {threshold: 0.05});
      this.observer.observe(this);
    }
    mount() {
      const source = this.mobile.matches && this.querySelector('template[data-home-video="mobile"]') || this.querySelector('template[data-home-video="desktop"]');
      if (!source || this.source === source) return;
      this.suspend(); this.video?.remove(); this.source = source;
      this.video = source.content.firstElementChild.cloneNode(true);
      this.video.removeAttribute('autoplay'); this.video.muted = true;
      this.video.controls = this.dataset.controls === 'true';
      const signal = this.abort.signal;
      this.video.addEventListener('play', () => { this.userPaused = false; this.updateToggle(); }, {signal});
      this.video.addEventListener('pause', () => {
        if (!this.ignorePause) this.userPaused = true;
        this.ignorePause = false; this.updateToggle();
      }, {signal});
      this.video.addEventListener('ended', () => this.updateToggle(), {signal});
      this.video.addEventListener('error', () => {
        // Preserve the cover and a usable native control rather than simulating playback.
        this.video.controls = true; if (this.toggle) this.toggle.hidden = true;
      }, {signal});
      this.append(this.video);
      if (this.toggle) this.toggle.hidden = false;
      this.updateToggle(); this.sync();
    }
    play() { this.video?.play()?.catch(() => { if (this.video) this.video.controls = true; this.updateToggle(); }); }
    suspend() {
      if (this.video && !this.video.paused) { this.ignorePause = true; this.video.pause(); }
    }
    sync() {
      if (!this.video) return;
      if (!this.inView || document.hidden || this.reduced.matches || window.Shopify?.designMode) this.suspend();
      else if (this.dataset.autoplay === 'true' && !this.userPaused) this.play();
    }
    updateToggle() {
      if (!this.toggle || !this.video) return;
      const paused = this.video.paused;
      this.toggle.setAttribute('aria-label', paused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel);
      this.toggle.querySelector('[data-home-play]').hidden = !paused;
      this.toggle.querySelector('[data-home-pause]').hidden = paused;
    }
    disconnectedCallback() {
      this.suspend(); this.observer?.disconnect(); this.abort?.abort(); this.abort = null;
      this.video?.remove(); this.video = null; this.source = null;
    }
  }
  customElements.define('aplovio-home-carousel', HomeCarousel);
  customElements.define('aplovio-home-media', HomeMedia);
})();
